import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';

/**
 * HoverVideo — vertical (9:16) autoplay-on-hover video with lazy thumbnail.
 * The first ~0.5s of the video is captured as a preview frame.
 */
const HoverVideo = ({ src, alt, aspect = '9/16', className = '' }) => {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const setMid = () => {
      if (!isNaN(v.duration) && v.duration > 0.2) {
        try { v.currentTime = Math.min(v.duration * 0.25, 1.5); } catch (_) {}
      }
      setReady(true);
    };
    if (v.readyState >= 1) setMid();
    else v.addEventListener('loadedmetadata', setMid, { once: true });
  }, [src]);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().then(() => setPlaying(true)).catch(() => {});
  };
  const pause = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    setPlaying(false);
  };

  return (
    <div
      className={`relative w-full overflow-hidden bg-black group/video ${className}`}
      style={{ aspectRatio: aspect }}
      onMouseEnter={play}
      onMouseLeave={pause}
      onTouchStart={play}
      onTouchEnd={pause}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        aria-label={alt}
      />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="absolute inset-0 bg-black/25" />
          <div className="relative w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl transition-transform group-hover/video:scale-110">
            <Play className="w-5 h-5 text-[#0a0a0b] ml-0.5" fill="#0a0a0b" />
          </div>
        </div>
      )}
      {!ready && <div className="absolute inset-0 bg-[#0a0a0b] animate-pulse" />}
    </div>
  );
};

export default HoverVideo;
